import React, { useCallback, useMemo } from 'react';

import { useStudentDispatch } from '../../hooks/useStudentDispatch';
import { Guid } from '../../store/model/guid';
import { addCompletedItem } from '../../store/reducers/student/tracking/itemsTrackingActions';
import { useStudentSelector } from '../../store/selectors/useStudentSelector';
import { NotImplementedException } from '../../utils/log';
import { createCompletionServiceSelector } from '../tracking/services/CompletionService';
import { ContentRequirements, ProgressBarStepType } from './ContentRequirements';
import { ContentItemType, isSection, Section, Widget } from '@ael/core-api';
import { observer } from 'mobx-react';

export type ContentRequirementsProviderProvided = {
  itemId: Guid;
  time: number;
  isRestricted: boolean;
  isRestrictedByListenTime: boolean;
  progressBarType: ProgressBarStepType;
  fulfilled: boolean;
  fulfillItemRequirements: () => void;
  submitWidget: (widget: Widget, correct: boolean, fulfillWithIncorrect?: boolean) => void;
};

interface ContentRequirementsProviderProps {
  item: ContentItemType | Section;
  children: ((props: ContentRequirementsProviderProvided) => React.ReactElement) | React.ReactElement;
  onWidgetSubmit?: (questionId: string, correct: boolean) => void;
}

const contentRequirementsContext = React.createContext<ContentRequirementsProviderProvided>({
  itemId: '',
  time: 0,
  isRestricted: false,
  isRestrictedByListenTime: false,
  fulfilled: true,
  progressBarType: ProgressBarStepType.Completed,
  fulfillItemRequirements: () => {
    throw NotImplementedException('Not implemented');
  },
  submitWidget: () => {
    throw NotImplementedException('Not implemented');
  },
});

export const { Provider } = contentRequirementsContext;

const ContentRequirementsProvider: React.FC<ContentRequirementsProviderProps> = observer(
  ({ children, item, onWidgetSubmit }) => {
    const dispatch = useStudentDispatch();
    const completionService = useStudentSelector(createCompletionServiceSelector);
    const wasCompleted = isSection(item) ? true : completionService.isComplete(item);
    const saveCompletedItem = useCallback(() => {
      dispatch(addCompletedItem(item.id));
    }, [dispatch, item.id]);

    const contentRequirements = useMemo(
      () => ContentRequirements.createContentRequirements(wasCompleted, item, saveCompletedItem),
      [item, saveCompletedItem, wasCompleted]
    );

    const provided = React.useMemo<ContentRequirementsProviderProvided>(() => {
      return {
        fulfilled: contentRequirements.fulfilled,
        fulfillItemRequirements: contentRequirements.fulfillItemRequirements,
        isRestrictedByListenTime: contentRequirements.isRestrictedByListenTime,
        time: contentRequirements.time,
        isRestricted: contentRequirements.isRestricted,
        submitWidget: (widget, correct, fulfillWithIncorrect) => {
          onWidgetSubmit?.(widget._id, correct);
          if (correct || fulfillWithIncorrect) {
            contentRequirements.fulfillWidget(widget);
          }
        },
        progressBarType: contentRequirements.progressBarStepType,
        itemId: item.id,
      };
    }, [contentRequirements, item.id, onWidgetSubmit]);

    return <Provider value={provided}>{typeof children === 'function' ? children(provided) : children}</Provider>;
  }
);

export const MemoizedContentRequirementsProvider = React.memo(ContentRequirementsProvider);

export const useContentRequirementsContext = () => React.useContext(contentRequirementsContext);
